import { useState, useEffect } from 'react';
import {
  AddListing,
  EditListing,
  EditRecurringEvent,
  ViewListing,
  selectionDialogProp,
} from '@/modules/SchedulingModule/components';
import {
  Experience,
  CreateListing,
  Listing,
  ProductTypes,
  BookingMethod,
} from '@/types';
import {
  useFeatureFlags,
  useLocations,
  useMerchantListings,
  useMerchantPlatformFee,
  useMutationListing,
} from '@/hooks';
import { DateClickArg } from '@fullcalendar/interaction';
import {
  calculatePriceWithPlatformFee,
  isListingModifiable,
  now,
  startOfMonth,
  endOfMonth,
} from '@/utils';
import {
  Button,
  useTheme,
  CalendarIcon,
  Box,
} from '@silverstein-properties/inspirelabs-ui';
import AlertDialog from '@/components/AlertDialog/AlertDialog';
import { DetailsDrawer } from '@/components';
import { AddListingInput, EditListingInput } from '@/classes';
import { DEFAULT_PLATFORM_FEE, EVENTS_FILTERS, VIEWS } from '@/constants';
import { useRecurringEvents } from '@/hooks/useRecurringEvents';
import { CancelListingInput } from '@/classes/CancelListingInput';
import SelectionDialog from '@/components/SelectionDialog/SelectionDialog';
import { BookingDetailsDrawer } from '@/modules/BookingModule';
import { Calendar } from '@/modules/SchedulingModule/components/Calendar';

interface BookingScheduleSectionProps {
  experience: Experience;
}

export const BookingScheduleSection = ({
  experience,
}: BookingScheduleSectionProps) => {
  const theme = useTheme();
  const { isCancelExperienceEventEnabled, isRecurringEventsEnabled } =
    useFeatureFlags();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newListing, setNewListing] = useState<CreateListing>({
    id: '',
    title: '',
    date: '',
    meetingPoint: undefined,
    description: '',
  });
  const [selectedListing, setSelectedListing] = useState<Listing>();
  const [listingError, setListingError] = useState<null | string>(null);
  const [editingListing, setEditingListing] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [showSelectionDialogCancellation, setShowSelectionDialogCancellation] =
    useState(false);
  const [showSelectionDialogEdit, setShowSelectionDialogEdit] = useState(false);
  const [editRecurringSeries, setEditRecurringSeries] = useState(false);
  const [changesInputted, setChangesInputted] = useState(
    !isRecurringEventsEnabled || false
  );

  const [selectedDate, setSelectedDate] = useState<Date>(now());
  const [eventsFilter, setEventsFilter] = useState<string>('all');
  const [selectedView, setSelectedView] = useState<string>(VIEWS.MONTH);

  const { data: venue } = useLocations({
    locationId:
      selectedListing?.meetingPoint?.id?.toString() ||
      experience?.location?.id?.toString() ||
      '',
  });
  const {
    data: merchantListingsPaginated,
    refetch: refetchListings,
    isLoading: isMerchantListingsLoading,
  } = useMerchantListings(
    {
      startsAt: startOfMonth(selectedDate),
      endsAt: endOfMonth(selectedDate),
      productIds: [experience?.id],
      hasBookings: eventsFilter === EVENTS_FILTERS.BOOKED,
    },
    !!experience?.id &&
      eventsFilter !== EVENTS_FILTERS.CUSTOMERS &&
      selectedView !== VIEWS.LIST
  );

  const listings = merchantListingsPaginated?.data || [];

  const {
    data: responseRecurringEventDto,
    refetch: refetchResponseRecurringEventDto,
  } = useRecurringEvents({
    recurringEventId: selectedListing?.recurringEventId,
  });

  const { data: platformFee } = useMerchantPlatformFee(
    ProductTypes.ONE_TIME_EVENT
  );

  useEffect(() => {
    if (venue && selectedListing) {
      setSelectedListing({
        ...selectedListing,
        meetingPoint: venue,
      });
    }
  }, [venue]);

  // Map the listings to FE data type once the listings are loaded for selected experience

  // calculates the end time of an event by adding the experience's duration to the start date
  const calculateEndTime = (startDate: Date) => {
    if (experience?.duration?.hours && experience?.duration?.minutes) {
      const endDate = new Date(startDate);
      const minutesToAdd =
        experience.duration.hours * 60 + experience.duration.minutes;
      endDate.setMinutes(endDate.getMinutes() + minutesToAdd);
      return endDate.getDay() !== startDate.getDay() ? undefined : endDate; // don't set end date if it will be next day
    }
    return undefined;
  };

  const handleCalendarClick = (arg: DateClickArg) => {
    if (
      experience?.bookingMethods?.includes(
        BookingMethod.REQUEST || BookingMethod.ADVERT
      )
    ) {
      return;
    }
    const today = new Date();
    setSelectedListing(undefined);
    let eventStart, eventEnd, eventDate; // if user is creating event on week/day view, split date into date and time
    if (arg.dateStr.includes('T')) {
      eventDate = arg.dateStr.split('T')[0];
      eventStart = arg.date;
      eventEnd = calculateEndTime(eventStart);
    }

    const netPrice = experience?.basePrice?.netAmount || experience?.price;
    let totalAmount =
      experience?.totalAmount || experience?.basePrice?.totalAmount;
    if (totalAmount === null) {
      totalAmount =
        parseFloat(
          calculatePriceWithPlatformFee(
            netPrice,
            platformFee || DEFAULT_PLATFORM_FEE,
            true
          )
        ) || 0;
    }

    if (!drawerOpen && today <= arg.date) {
      setNewListing({
        id: experience?.id,
        title: experience?.title || '',
        date: eventDate || arg.dateStr,
        startsAt: eventStart || undefined,
        endsAt: eventEnd || undefined,
        price: netPrice,
        totalAmount,
        meetingPoint: venue || undefined,
        description: experience?.location?.info || '',
      });
      setListingError(null);
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleEventClick = async (id: string) => {
    const clickedListing = listings?.find(listing => listing.id === id);
    if (!clickedListing) {
      return;
    }
    setSelectedListing(clickedListing);
    setDrawerOpen(!drawerOpen);
  };

  const closeWindow = () => {
    setDrawerOpen(false);
    setListingError(null);
    setEditingListing(false);
    setChangesInputted(false);
  };

  const onMutationSuccess = () => {
    setListingError(null);
    refetchListings();
    refetchResponseRecurringEventDto();
    closeWindow();
  };

  const onMutationError = (message: string) => {
    setListingError(message);
  };

  // updating the listing to being "close" which does not close the drawer
  const onMutationCloseListingSuccess = () => {
    setListingError(null);
    refetchListings();
    refetchResponseRecurringEventDto();
  };

  const {
    addListingMutation: { mutate: addListing, isLoading: isAddListingLoading },
    editListingMutation: {
      mutate: editListing,
      isLoading: isEditListingLoading,
    },
    cancelListingMutation: {
      mutate: cancelListing,
      isLoading: isCancelListingLoading,
    },
  } = useMutationListing(
    { selectedExperience: experience, selectedListing },
    onMutationSuccess,
    onMutationError
  );

  const handleSubmit = async (data: AddListingInput) => {
    addListing(data);
  };

  const handleEditListingSubmit = async (data: EditListingInput) => {
    editListing(data);
  };

  const handleViewListingCancel = async (data: CancelListingInput) => {
    cancelListing(data);
  };

  const handleCloseAlertDialog = (selected: boolean) => {
    setAlertDialogOpen(false);
    if (selected) {
      handleViewListingCancelListing();
    } else {
      closeWindow();
    }
  };

  const handleViewListingCancelListing = async () => {
    try {
      handleViewListingCancel({
        listingId: selectedListing?.id || '',
        editRecurringSeries: false,
      });

      setListingError(null); // if a previous attempt resulted in error, remove error banner if this is successful
    } catch (err) {
      console.error(err);
      setListingError("We couldn't cancel your listing");
      return;
    }
    // Once the listing gets created, refetch listings hook
    refetchListings();
    closeWindow();
  };

  const handleViewListingSubmit = () => {
    // TODO: submit logic for saving a "Published" listing goes here
    console.log('submit view listing');
  };

  const handleSelectionDialogCancellation = (selected: string | undefined) => {
    setShowSelectionDialogCancellation(false);

    if (selected !== undefined) {
      handleViewListingCancel({
        recurringEventId: responseRecurringEventDto?.recurringEvent?.id,
        listingId: selectedListing?.id || '',
        editRecurringSeries: selected === EditRecurringEvent.isRecurringEvent,
      });
    }
    // if cancel is selected - do nothing
  };

  const handleSelectionDialogEdit = (selected: string | undefined) => {
    setShowSelectionDialogEdit(false);

    if (selected !== undefined) {
      setEditRecurringSeries(selected === EditRecurringEvent.isRecurringEvent);
      setEditingListing(true);
    }
    // if cancel is selected - do nothing
  };

  const toggleEdit = () => {
    if (responseRecurringEventDto?.recurringEvent) {
      // setEditRecurringSeries(false); // default value
      setShowSelectionDialogEdit(true);
    } else {
      setEditRecurringSeries(false);
      setEditingListing(true);
    }
  };

  const calculateExperienceLength = () =>
    experience?.duration?.hours !== undefined &&
    experience?.duration?.minutes !== undefined
      ? experience.duration.hours * 60 + experience.duration.minutes
      : 0;

  // Determines which title and screen to show in the DetailsDrawer
  let title = '';
  let detailsDrawerType: 'Add' | 'Edit' | 'View';
  if (!editingListing) {
    detailsDrawerType = selectedListing ? 'View' : 'Add';
  } else {
    detailsDrawerType = 'Edit';
  }
  switch (detailsDrawerType) {
    case 'Add':
      title = 'Add Event';
      break;
    case 'Edit':
      title = 'Edit Event Details';
      break;
    case 'View':
    default:
      title = 'Event Details';
      break;
  }

  // Edit action for the view listing screen
  const actionTop =
    !!isListingModifiable(selectedListing) && detailsDrawerType === 'View' ? (
      <Button size="small" onClick={toggleEdit}>
        Edit
      </Button>
    ) : undefined;

  // Different actions depending on the screen user is on
  const actions =
    detailsDrawerType === 'Add' ? (
      <>
        <Button variant="text" onClick={closeWindow}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isAddListingLoading}
          form="addListingForm"
        >
          Save
        </Button>
      </>
    ) : detailsDrawerType === 'Edit' ? (
      changesInputted ? (
        <>
          <Button variant="text" onClick={closeWindow}>
            Discard
          </Button>
          <Button
            type="submit"
            disabled={isEditListingLoading || isCancelListingLoading}
            form="editListingForm"
          >
            Publish Changes
          </Button>
        </>
      ) : (
        <Button variant="text" onClick={closeWindow}>
          Cancel
        </Button>
      )
    ) : detailsDrawerType === 'View' ? (
      !isCancelExperienceEventEnabled &&
      isListingModifiable(selectedListing) ? (
        <Button
          disabled={isCancelListingLoading}
          variant="text"
          onClick={() => {
            if (
              isRecurringEventsEnabled &&
              !!responseRecurringEventDto?.recurringEvent
            ) {
              setShowSelectionDialogCancellation(true);
            } else {
              setAlertDialogOpen(true);
            }
          }}
          // It suppose to have "spacers.xl : 32px" but there are "spacers.m : 16px" of paddings
          // added in this file already: src /components/DetailsDrawer/DetailsDrawer.styles.ts (line 13)
          sx={{ color: 'error.main', marginY: theme.spacers.m }}
        >
          Cancel event
        </Button>
      ) : undefined
    ) : undefined;

  // send proper component to details drawer, depending on user intent(add, view, edit)
  return (
    <>
      {isRecurringEventsEnabled && showSelectionDialogEdit && (
        <SelectionDialog
          open={showSelectionDialogEdit}
          onClose={handleSelectionDialogEdit}
          image={<CalendarIcon />}
          title="Edit Reoccurring Event"
          selections={selectionDialogProp}
          agree="Confirm"
          disagree="Cancel"
        />
      )}
      {isRecurringEventsEnabled && showSelectionDialogCancellation && (
        <SelectionDialog
          open={showSelectionDialogCancellation}
          onClose={handleSelectionDialogCancellation}
          image={<CalendarIcon />}
          title="Cancel Reoccurring Event"
          selections={selectionDialogProp}
          agree="Confirm"
          disagree="Cancel"
        />
      )}
      {alertDialogOpen && (
        <AlertDialog
          open={alertDialogOpen}
          onClose={handleCloseAlertDialog}
          title="Cancel Event"
          message={
            selectedListing?.hasBookings
              ? 'You are about to cancel event for all participants. We will notify all participants under this event.'
              : 'Are you sure you want to cancel this event?'
          }
          // TODO: we don't have the number of users easily available q_q
          agree="Yes"
          disagree="No"
        />
      )}
      <Box sx={{ position: 'relative', pb: theme.spacers.xxl8 }}>
        <Calendar
          filters={{
            eventsType: eventsFilter,
            productsIds: [experience?.id],
          }}
          products={[experience]}
          onFiltersChange={filters => {
            setEventsFilter(filters.eventsType || '');
          }}
          onDateChange={setSelectedDate}
          onCalendarClick={handleCalendarClick}
          selectedDate={selectedDate}
          onEventClick={handleEventClick}
          isLoading={isMerchantListingsLoading}
          events={listings || []}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          headerVariant="small"
          id={experience?.id}
        />
      </Box>
      <DetailsDrawer
        actions={actions}
        actionTop={actionTop}
        closeWindow={closeWindow}
        drawerOpen={drawerOpen}
        title={title}
        // TODO: improve verification to see if a listing can be editing
        // ie: dates in the past and within 24hours of listing startDate
      >
        {detailsDrawerType === 'Add' ? (
          <AddListing
            handleSubmit={handleSubmit}
            listing={newListing}
            listingError={listingError}
            experienceType={experience?.type}
            experienceLength={calculateExperienceLength()}
          />
        ) : detailsDrawerType === 'Edit' && selectedListing ? (
          //  if listing is recurring send to edit otherwise send to old
          // will likely be changed but waiting to hear back from Product
          <EditListing
            handleSubmit={handleEditListingSubmit}
            listing={selectedListing}
            recurringEvent={responseRecurringEventDto?.recurringEvent}
            listingError={listingError}
            experienceLength={calculateExperienceLength()}
            editRecurringSeries={editRecurringSeries}
            experience={experience}
            setChangesInputted={setChangesInputted}
          />
        ) : detailsDrawerType === 'View' && selectedListing ? (
          <ViewListing
            handleSubmit={handleViewListingSubmit}
            listing={selectedListing}
            venue={venue}
            recurringEvent={responseRecurringEventDto?.recurringEvent}
            listingError={listingError}
            experience={experience}
            onMutationCloseListingSuccess={onMutationCloseListingSuccess}
          />
        ) : null}
        <BookingDetailsDrawer />
      </DetailsDrawer>
    </>
  );
};
